import { useEffect } from 'react';
import { useGenericBi } from './use-generic-bi';
import { isIosSafari } from './app-banner';

/**
 * Best-effort impression for Apple's native Smart App Banner on iOS Safari.
 *
 * On iOS Safari the banner is drawn by WebKit from the `apple-itunes-app` <meta>
 * (SymphonyApp) as OS chrome — the page receives NO impression, tap or dismiss callback
 * from it, so that surface would otherwise be a total analytics blind spot. This fires a
 * `loadFinish` (198:5) beacon named `native-app-banner` once on mount when iOS Safari is
 * detected, so we at least have a denominator of sessions the banner was offered to.
 *
 * Deliberate limitations, documented rather than papered over:
 * - It CANNOT capture the Open/Get tap or the dismiss — Safari exposes neither.
 * - It OVER-COUNTS: Safari suppresses its own banner once dismissed (or when its
 *   heuristics don't fire), and the page can't see that, so this logs "offered" even
 *   when nothing was actually painted.
 * - It fires only where `AppFrame` mounts, so chrome-free routes (where the app-wide
 *   meta still shows a banner) are slightly under-counted.
 *
 * The custom `AppBanner` (which fires its own impression + Open/Get + dismiss) is
 * suppressed on iOS Safari, so the two impressions are mutually exclusive — no surface
 * double-logs.
 */
export function useNativeAppBannerImpression(): void {
  const { logLoadFinish } = useGenericBi();
  useEffect(() => {
    if (!isIosSafari()) return;
    logLoadFinish(
      'native-app-banner',
      { isSuccessful: true },
      { additionalFields: { surface: 'ios-safari' } },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
